<template>
  <PublicProfileLayout>
    <section class="tutor-results-section px-4 md:px-6 py-6">
      <div class="w-full md:w-4">
        <div class="p-inputgroup">
          <InputGroup>
            <InputText v-model="searchTerm" placeholder="Search by tutor name, subject, class, or keywords..."
              variant="filled" />
            <InputGroupAddon>
              <Button icon="pi pi-search" severity="secondary" variant="text" @click="toggle" class="search" />
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>

      <div class="flex flex-column md:flex-row justify-content-end align-items-center mb-4 pt-2">
        <div class="flex align-items-center gap-2">
          <span class="text-sm text-gray-600">Sort by:</span>
          <Select v-model="sortOption" :options="sortOptions" optionLabel="label" optionValue="value"
            placeholder="Select" size="small" />
        </div>
      </div>

      <!-- Dropdowns: 4-column grid, fills 2 rows -->
      <div class="filter-grid mb-3">
        <Select v-for="(options, key) in filterOptions" :key="key" v-model="filters[key]" :options="options"
          optionLabel="label" optionValue="value" :placeholder="filterPlaceholders[key] || capitalize(key)" size="small"
          class="filter-select border-round-md border-1 surface-border text-sm" />
      </div>

      <!-- Price + action row: price inputs left, buttons right with gap -->
      <div class="filter-bottom-row mb-5 md:pb-8">
        <div class="price-group">
          <InputNumber v-model="minPrice" placeholder="Min Price" size="small" class="price-input" />
          <InputNumber v-model="maxPrice" placeholder="Max Price" size="small" class="price-input" />
        </div>
        <div class="button-group">
          <Button label="Apply Filters" @click="applyFilters" size="small"
            class="teal-button-small border-round-md text-sm" />
          <Button label="Clear All" icon="pi pi-times" @click="clearFilters" size="small" severity="secondary"
            class="teal-button-transparent-small" />
        </div>
      </div>

      <div v-if="tutors.length > 0" class="grid">
        <div v-for="(tutor, i) in tutors" :key="i" class="col-12 sm:col-6 md:col-4 lg:col-3 xxl:col-5ths md:px-4 py-3 flex">
          <div class="card border-round-lg overflow-hidden shadow-2 hover:shadow-4 transition-all transition-duration-200 flex flex-column w-full h-full"
            @click="tutorProfile(tutor._id)">
            
            <div class="card-image-wrapper surface-100 flex align-items-center justify-content-center">
              <img
                :src="isValidPhoto(tutor) ? usePublicUrl(tutor.professionalPhotoURL).value : blankProfile"
                alt="Tutor"
                class="card-image-cover"
                @error="handleImageError"
              />
            </div>

            <div class="p-3 flex flex-column justify-content-between flex-grow-1 gap-3">
              <div class="card-top">
                <h4 class="H6-Medium mb-1 tutor-name" :title="tutor.username">{{ tutor.username }}</h4>
                <p class="Caption mb-1 mt-0">{{ cleanupLables(tutor.tutorType) }}</p>
                <p class="Caption mb-2 mt-0 tutor-languages text-color-secondary" :title="tutor.teachingLanguage.map(l => cleanupLables(l)).join(' / ')">
                  {{ tutor.teachingLanguage.map(l => cleanupLables(l)).join(" / ") }} {{ tutor.teachingLanguage.length === 1 ? 'Medium' : 'Mediums' }}
                </p>
                
                <div class="flex flex-wrap gap-1 chip-area" @click.stop>
                  <span v-for="subject in getVisibleSubjects(tutor)" :key="subject" class="subject-chip">
                    {{ subject }}
                  </span>
                  
                  <span v-if="getRemainingCount(tutor) > 0" 
                        class="subject-chip chip-more cursor-pointer hover:surface-300 transition-colors" 
                        @click="(e) => showMoreSubjects(e, tutor, i)">
                    +{{ getRemainingCount(tutor) }} more
                  </span>
                </div>
              </div>

              <div class="card-bottom mt-auto">
                <div class="flex align-items-center gap-2 mb-1">
                  <span class="Paragraph-Medium">{{ tutor.averageRating }}</span>
                  <span class="Caption">({{ tutor.reviewCount }} reviews)</span>
                </div>
                
                <div class="flex align-items-center justify-content-between mt-1" @click.stop>
                  <p class="Paragraph-Medium mt-0 mb-0">{{ getTutorPrice(tutor) }}</p>
                  <span v-if="tutor.teachingLevels && tutor.teachingLevels.length > 0" 
                        class="text-xs text-primary cursor-pointer hover:underline font-medium flex align-items-center gap-1"
                        @click="(e) => showPricingDetails(e, tutor)">
                    <i class="pi pi-info-circle" style="font-size: 0.8rem"></i> Rates
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else class="empty-state">
        <template v-if="hasActiveFilters">
          <i class="pi pi-filter-slash empty-state-icon"></i>
          <p class="empty-state-title">No tutors match your filters</p>
          <p class="empty-state-sub">Try adjusting or clearing your filters to see more results.</p>
          <Button label="Clear Filters" icon="pi pi-times" @click="clearFilters" size="small" severity="secondary"
            class="teal-button-transparent-small mt-3" />
        </template>
        <template v-else>
          <i class="pi pi-users empty-state-icon"></i>
          <p class="empty-state-title">No tutors available yet</p>
          <p class="empty-state-sub">Check back soon as new tutors are joining every day.</p>
        </template>
      </div>

      <OverlayPanel ref="op" class="w-18rem">
        <div class="p-2">
          <span class="text-xs font-semibold text-500 block mb-2 uppercase">All Subjects</span>
          <div class="flex flex-wrap gap-1">
            <span v-for="sub in activePopoverSubjects" :key="sub" class="subject-chip">
              {{ sub }}
            </span>
          </div>
        </div>
      </OverlayPanel>

      <OverlayPanel ref="pricingOp" class="w-22rem md:w-26rem shadow-4 border-round-lg">
        <div class="p-3 max-h-25rem overflow-y-auto">
          <div class="flex justify-content-between align-items-center mb-3">
            <span class="text-xs font-semibold text-500 uppercase tracking-wide">Teaching Levels & Rates</span>
            <Button icon="pi pi-times" variant="text" severity="secondary" rounded @click="pricingOp.hide()" class="w-2rem h-2rem p-0" />
          </div>
          
          <div v-for="(subjectsList, gradeName) in groupedTeachingLevels" :key="gradeName" class="mb-4 last:mb-0">
            <div class="surface-100 border-round px-2 py-1 mb-2 border-primary text-center">
              <span class="text-sm font-bold text-gray-800 tracking-wide">{{ cleanupGrade(gradeName) }}</span>
            </div>

            <div v-for="(level, idx) in subjectsList" :key="idx" class="ml-2 mb-3 pb-2 border-bottom-1 surface-border last:border-none last:pb-0">
              <span class="text-sm font-semibold text-gray-800 block mb-1">{{ cleanupLables(level.subject) }}</span>
              
              <div class="flex flex-column gap-1 px-2 py-1 surface-50 border-round">
                <div v-if="level.individualClass?.available" class="flex justify-content-between align-items-center text-xs">
                  <span class="text-600">Individual Class:</span>
                  <span class="font-medium text-primary">{{ formatPrice(level.individualClass.charge) }}</span>
                </div>
                <div v-if="level.groupClass?.available" class="flex justify-content-between align-items-center text-xs">
                  <span class="text-600">Group Class:</span>
                  <span class="font-medium text-primary">{{ formatPrice(level.groupClass.charge) }}</span>
                </div>
                <div v-if="!level.individualClass?.available && !level.groupClass?.available" class="text-xs text-500 italic">
                  No active pricing setup
                </div>
              </div>
            </div>
          </div>
        </div>
      </OverlayPanel>

    </section>
  </PublicProfileLayout>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { mixpanelService } from '@/services/mixpanel';
import PublicProfileLayout from "@/layout/PublicProfileLayout.vue";
import InputNumber from 'primevue/inputnumber';
import blankProfile from '@/assets/blank-profile.png';
import Select from 'primevue/select';
import InputGroup from 'primevue/inputgroup';
import InputText from 'primevue/inputtext';
import { fetchPublicProfiles } from '@/api/tutor';
import InputGroupAddon from 'primevue/inputgroupaddon';
import Button from 'primevue/button';
import OverlayPanel from 'primevue/overlaypanel';
import { useRouter } from "vue-router";
import {
  availabilityOptions,
  gender,
  occupation,
  languages,
  gradeOptions,
  fetchGradeOptions,
} from '@/utils/options';
import { usePublicUrl } from "@/utils/getPublicUrl";
import { cleanupLables } from "@/utils/cleanups";

const router = useRouter();
const sortOption = ref("newest");
const searchTerm = ref("");
const minPrice = ref();
const maxPrice = ref();
let searchTimeout = null;
const tutors = ref([]);

// Popover tracking targets
const op = ref(null);
const pricingOp = ref(null);
const activePopoverSubjects = ref([]);
const groupedTeachingLevels = ref({}); // Now dynamic mapping state objects instead of standard single level loops

const sortOptions = [
  { label: 'Newest', value: 'newest' },
  { label: 'Oldest', value: 'oldest' },
];

const filters = ref({
  location: "",
  language: "",
  gender: "",
  classMode: "",
  tutorType: "",
  rating: "",
  grade: "",
  subject: "",
});

const filterOptions = reactive({
  location: [],
  language: languages.map(lang => ({ label: lang.value, value: lang.key })),
  gender: gender.map(g => ({ label: g.label, value: g.value })),
  classMode: availabilityOptions.map(opt => ({ label: opt.value, value: opt.key })),
  tutorType: occupation.map(occ => ({ label: occ.value, value: occ.key })),
  rating: [
    { label: "4 and above", value: "4" },
    { label: "3 and above", value: "3" },
    { label: "2 and above", value: "2" },
    { label: "1 and above", value: "1" }
  ],
  grade: [],
  subject: [],
});

const filterPlaceholders = {
  location: "Location",
  language: "Language",
  gender: "Gender",
  classMode: "Class Mode",
  tutorType: "Tutor Type",
  rating: "Rating",
  grade: "Grade",
  subject: "Subject",
};

const hasActiveFilters = computed(() =>
  searchTerm.value.trim() !== "" ||
  minPrice.value != null ||
  maxPrice.value != null ||
  Object.values(filters.value).some(v => v != null && v !== "")
);

watch(searchTerm, (newValue) => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    if (newValue.trim() !== "" || newValue === "") {
      applyFilters();
    }
  }, 300);
});

const applyFilters = () => {
  fetchTutors();
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const fetchTutors = async () => {
  try {
    const params = {
      searchTerm: searchTerm.value,
      sortBy: sortOption.value,
      minPrice: minPrice.value,
      maxPrice: maxPrice.value,
      ...filters.value
    };

    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === "") {
        delete params[key];
      }
    });

    const profiles = await fetchPublicProfiles(params);
    tutors.value = profiles.data.data.tutors.map(tutor => {
      return {
        ...tutor,
        lastLocationWord: getLastWord(tutor.address)
      };
    });

    mixpanelService.track('Tutor Search', {
      ...params,
      result_count: tutors.value.length,
      timestamp: new Date().getTime()
    });

    const uniqueLocations = [
      ...new Set(tutors.value.map(tutor => tutor.lastLocationWord))
    ].filter(Boolean);

    filterOptions.location = uniqueLocations.map(loc => ({
      label: capitalize(loc),
      value: loc
    }));
  } catch (err) {
    const status = err?.response?.status ?? err?.response?.data?.code;
    if (status === 404) {
      tutors.value = [];
    }
  }
};

const allSubjectOptions = ref([]);

const buildSubjectList = (grades) => {
  const seen = new Set();
  const merged = [];
  for (const grade of grades) {
    for (const sub of (grade.subjects || [])) {
      if (sub.code && !seen.has(sub.code)) {
        seen.add(sub.code);
        merged.push({ label: sub.name, value: sub.code });
      }
    }
  }
  return merged.sort((a, b) =>
    a.label.split(' ')[0].localeCompare(b.label.split(' ')[0])
  );
};

watch(() => filters.value.grade, (selectedGrade) => {
  if (selectedGrade) {
    const matched = gradeOptions.value.find(g => g.code === selectedGrade);
    filterOptions.subject = matched
      ? (matched.subjects || []).map(s => ({ label: s.name, value: s.code }))
          .sort((a, b) => a.label.split(' ')[0].localeCompare(b.label.split(' ')[0]))
      : allSubjectOptions.value;
  } else {
    filterOptions.subject = allSubjectOptions.value;
  }
  filters.value.subject = "";
});

onMounted(async () => {
  await fetchGradeOptions();
  filterOptions.grade = gradeOptions.value.map(g => ({ label: g.name, value: g.code }));
  allSubjectOptions.value = buildSubjectList(gradeOptions.value);
  filterOptions.subject = allSubjectOptions.value;
  await fetchTutors();
});

const getLastWord = (address) => {
  if (!address) return "";
  const words = address.trim().split(/\s+/);
  return words[words.length - 1];
};

const clearFilters = () => {
  filters.value = {
    location: null,
    language: null,
    gender: null,
    classMode: null,
    tutorType: null,
    rating: null,
    grade: null,
    subject: null,
  };
  minPrice.value = null;
  maxPrice.value = null;
  searchTerm.value = "";
  sortOption.value = "newest";
  fetchTutors();
};

const tutorProfile = (tutorId) => {
  router.push(`/tutorProfile/${tutorId}`);
};

const isValidPhoto = (tutor) => !!tutor?.professionalPhotoURL;

const handleImageError = (event) => {
  event.target.src = blankProfile;
};

// Enforces exact string structuring patterns ('Rs. 100.00')
const formatPrice = (value) => {
  const parsed = parseFloat(value);
  if (isNaN(parsed)) return "LKR. 0.00";
  return `LKR. ${parsed.toFixed(2)}`;
};

// Strips out underlying system separators from plain grade identifiers
const cleanupGrade = (grade) => {
  if (!grade) return "General Level";
  return grade.replace(/_/g, " ");
};

const getTutorPrice = (tutor) => {
  const levels = tutor.teachingLevels ?? [];
  const charges = levels.flatMap(l => [
    l.groupClass?.available ? l.groupClass.charge : null,
    l.individualClass?.available ? l.individualClass.charge : null,
  ]).filter(c => c != null && c > 0);
  
  if (!charges.length) return "Contact for pricing";
  const min = Math.min(...charges);
  if (charges.length === 1) return `${formatPrice(min)}/hour`;
  return `From ${formatPrice(min)}/hour`;
};

const MAX_VISIBLE_SUBJECTS = 3;

const getAllSubjects = (tutor) => {
  const levels = tutor.teachingLevels ?? [];
  const raw = levels.flatMap(l =>
    l.subject ? [l.subject] : Array.isArray(l.subjects) ? l.subjects : []
  );
  return [...new Set(raw)].sort((a, b) => a.localeCompare(b)).map(s => cleanupLables(s));
};

const getVisibleSubjects = (tutor) =>
  getAllSubjects(tutor).slice(0, MAX_VISIBLE_SUBJECTS);

const getRemainingCount = (tutor) =>
  Math.max(0, getAllSubjects(tutor).length - MAX_VISIBLE_SUBJECTS);

const showMoreSubjects = (event, tutor) => {
  activePopoverSubjects.value = getAllSubjects(tutor);
  op.value.toggle(event);
};

// Efficient array accumulator logic sorting items directly cleanly based on parent grades
const showPricingDetails = (event, tutor) => {
  const levelsRaw = tutor.teachingLevels ?? [];
  
  const grouped = levelsRaw.reduce((accumulator, currentLevel) => {
    const targetGradeKey = currentLevel.grade || "Uncategorized";
    if (!accumulator[targetGradeKey]) {
      accumulator[targetGradeKey] = [];
    }
    accumulator[targetGradeKey].push(currentLevel);
    return accumulator;
  }, {});

  groupedTeachingLevels.value = grouped;
  pricingOp.value.toggle(event);
};
</script>

<style scoped>
.tutor-results-section {
  background-color: #fff;
}

.card {
  cursor: pointer;
  transition: box-shadow 0.2s ease-in-out;
  min-height: 380px; 
}

.card-image-wrapper {
  flex-shrink: 0;
  height: 200px;
  width: 100%;
  overflow: hidden;
}

.card-image-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

::v-deep(.p-inputtext) {
  --p-inputtext-border-color: #368789 !important;
}

::v-deep(.p-inputgroupaddon) {
  --p-inputgroup-addon-background: #368789 !important;
  --p-inputgroup-addon-background-hover: #368789 !important;
  --p-inputgroup-addon-color: white;
}

.search {
  color: white !important;
}

.search:hover {
  color: #368789 !important;
  background-color: #368789;
}

.card-top {
  width: 100%;
}

.card-bottom {
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  width: 100%;
}

.tutor-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tutor-languages {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip-area {
  width: 100%;
}

.subject-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: 20px;
  border: 1px solid #d1d5db;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 10px;
  font-weight: 400;
  line-height: 1;
}

.border-primary {
  border-color: #368789 !important;
}

.text-primary {
  color: #368789 !important;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 600px);
  text-align: center;
  padding: 2rem 1rem;
}

.empty-state-icon {
  font-size: 2.5rem;
  color: var(--surface-400);
  margin-bottom: 1rem;
}

.empty-state-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 0.4rem;
}

.empty-state-sub {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  margin: 0;
  max-width: 320px;
}

/* Dropdowns — single row on large screens, breaks into 2 rows then 2-col on smaller */
.filter-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
}

.filter-select {
  width: 100%;
}

/* Price + buttons row */
.filter-bottom-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-group {
  display: flex;
  gap: 8px;
  flex: 1;
}

.price-input {
  flex: 1;
}

::v-deep(.price-input .p-inputnumber-input) {
  width: 100%;
}

.button-group {
  display: flex;
  gap: 8px;
}

@media (min-width: 1000px) {
  .button-group {
    margin-left: 20%;
  }
}

@media (min-width: 1200px) {
  .button-group {
    margin-left: 40%;
  }
}

/* Medium: 4 dropdowns per row (2 rows) */
@media (max-width: 1024px) {
  .filter-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Small: 2 dropdowns per row, price+buttons stack */
@media (max-width: 640px) {
  .filter-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .filter-bottom-row {
    flex-wrap: wrap;
  }

  .price-group {
    width: 100%;
  }

  .button-group {
    width: 100%;
    margin-left: 0;
  }

  .button-group :deep(.p-button) {
    flex: 1;
    justify-content: center;
  }

  ::v-deep(.price-input .p-inputnumber-input) {
    width: 100%;
  }
}

@media (min-width: 1400px) {
  .xxl\:col-5ths {
    width: 20% !important;
  }
}
</style>